// 定义类型接口
interface URLSearchParamsHelperInstance {
  params: URLSearchParams;
  _parseManually(): [string, string][];
  forEach(callback: (value: string, key: string, params: URLSearchParams) => void, thisArg?: any): void;
  entries(): any;
  keys(): any;
  values(): any;
  toObject(): Record<string, string | string[]>;
  toArray(): [string, string][];
}

// 使用类的方式重写，确保类型安全
export class URLSearchParamsHelper implements URLSearchParamsHelperInstance {
  params: URLSearchParams;

  constructor(searchParams: URLSearchParams) {
    this.params = searchParams;
  }

  // 私有方法：手动解析参数字符串
  _parseManually(): [string, string][] {
    const result: [string, string][] = [];
    const paramString = this.params.toString();

    if (!paramString) {
      return result;
    }

    const pairs = paramString.split('&');

    for (let i = 0; i < pairs.length; i++) {
      const pair = pairs[i];
      const equalIndex = pair.indexOf('=');

      if (equalIndex === -1) {
        result.push([decodeURIComponent(pair), '']);
      } else {
        const key = decodeURIComponent(pair.substring(0, equalIndex));
        const value = decodeURIComponent(pair.substring(equalIndex + 1));
        result.push([key, value]);
      }
    }

    return result;
  }

  // 遍历方法
  forEach(callback: (value: string, key: string, params: URLSearchParams) => void, thisArg?: any): void {
    if (this.params && typeof (this.params as any).forEach === 'function') {
      try {
        (this.params as any).forEach(callback, thisArg);
      } catch (error) {
        // 如果原生forEach失败，使用手动解析
        const pairs = this._parseManually();
        for (let i = 0; i < pairs.length; i++) {
          callback.call(thisArg, pairs[i][1], pairs[i][0], this.params);
        }
      }
    } else {
      const pairs = this._parseManually();
      for (let i = 0; i < pairs.length; i++) {
        callback.call(thisArg, pairs[i][1], pairs[i][0], this.params);
      }
    }
  }

  // 获取所有键值对
  entries(): any {
    // 检查 entries 方法是否存在且为函数
    if (this.params && typeof (this.params as any).entries === 'function') {
      try {
        return (this.params as any).entries();
      } catch (error) {
        // 如果原生entries失败，使用手动解析
        const pairs = this._parseManually();
        let index = 0;

        const iterator = {
          next(): IteratorResult<[string, string]> {
            if (index < pairs.length) {
              return { value: pairs[index++], done: false };
            } else {
              return { value: undefined as any, done: true };
            }
          }
        };

        // 条件性添加Symbol.iterator以确保兼容性
        if (typeof Symbol !== 'undefined' && Symbol.iterator) {
          (iterator as any)[Symbol.iterator] = function () {
            return this;
          };
        }

        return iterator;
      }
    } else {
      const pairs = this._parseManually();
      let index = 0;

      const iterator = {
        next(): IteratorResult<[string, string]> {
          if (index < pairs.length) {
            return { value: pairs[index++], done: false };
          } else {
            return { value: undefined as any, done: true };
          }
        }
      };

      // 条件性添加Symbol.iterator以确保兼容性
      if (typeof Symbol !== 'undefined' && Symbol.iterator) {
        (iterator as any)[Symbol.iterator] = function () {
          return this;
        };
      }

      return iterator;
    }
  }

  // 获取所有键
  keys(): any {
    if (this.params && typeof (this.params as any).keys === 'function') {
      try {
        return (this.params as any).keys();
      } catch (error) {
        // 如果原生keys失败，使用手动解析
        const pairs = this._parseManually();
        const keys = pairs.map((pair) => pair[0]);
        let index = 0;

        const iterator = {
          next(): IteratorResult<string> {
            if (index < keys.length) {
              return { value: keys[index++], done: false };
            } else {
              return { value: undefined as any, done: true };
            }
          }
        };

        // 条件性添加Symbol.iterator以确保兼容性
        if (typeof Symbol !== 'undefined' && Symbol.iterator) {
          (iterator as any)[Symbol.iterator] = function () {
            return this;
          };
        }

        return iterator;
      }
    } else {
      const pairs = this._parseManually();
      const keys = pairs.map((pair) => pair[0]);
      let index = 0;

      const iterator = {
        next(): IteratorResult<string> {
          if (index < keys.length) {
            return { value: keys[index++], done: false };
          } else {
            return { value: undefined as any, done: true };
          }
        }
      };

      // 条件性添加Symbol.iterator以确保兼容性
      if (typeof Symbol !== 'undefined' && Symbol.iterator) {
        (iterator as any)[Symbol.iterator] = function () {
          return this;
        };
      }

      return iterator;
    }
  }

  // 获取所有值
  values(): any {
    if (this.params && typeof (this.params as any).values === 'function') {
      try {
        return (this.params as any).values();
      } catch (error) {
        // 如果原生values失败，使用手动解析
        const pairs = this._parseManually();
        const values = pairs.map((pair) => pair[1]);
        let index = 0;

        const iterator = {
          next(): IteratorResult<string> {
            if (index < values.length) {
              return { value: values[index++], done: false };
            } else {
              return { value: undefined as any, done: true };
            }
          }
        };

        // 条件性添加Symbol.iterator以确保兼容性
        if (typeof Symbol !== 'undefined' && Symbol.iterator) {
          (iterator as any)[Symbol.iterator] = function () {
            return this;
          };
        }

        return iterator;
      }
    } else {
      const pairs = this._parseManually();
      const values = pairs.map((pair) => pair[1]);
      let index = 0;

      const iterator = {
        next(): IteratorResult<string> {
          if (index < values.length) {
            return { value: values[index++], done: false };
          } else {
            return { value: undefined as any, done: true };
          }
        }
      };

      // 条件性添加Symbol.iterator以确保兼容性
      if (typeof Symbol !== 'undefined' && Symbol.iterator) {
        (iterator as any)[Symbol.iterator] = function () {
          return this;
        };
      }

      return iterator;
    }
  }

  // 转换为普通对象
  toObject(): Record<string, string | string[]> {
    const result: Record<string, string | string[]> = {};

    this.forEach((value: string, key: string) => {
      if (result.hasOwnProperty(key)) {
        if (Array.isArray(result[key])) {
          (result[key] as string[]).push(value);
        } else {
          result[key] = [result[key] as string, value];
        }
      } else {
        result[key] = value;
      }
    });

    return result;
  }

  // 转换为数组
  toArray(): [string, string][] {
    const result: [string, string][] = [];

    this.forEach((value: string, key: string) => {
      result.push([key, value]);
    });

    return result;
  }
}

// 为了向后兼容，保留函数式调用方式
export function createURLSearchParamsHelper(searchParams: URLSearchParams): URLSearchParamsHelper {
  return new URLSearchParamsHelper(searchParams);
}
